import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { collection, getDocs } from "firebase/firestore";
import teams from "./teams";

const apiIdToTeam = {};
teams.forEach((t) => { if (t.apiId) apiIdToTeam[t.apiId] = t; });

function Flag({ cc, size = 14 }) {
  if (!cc) return null;
  const h = Math.round(size * 0.75);
  return (
    <img src={"https://flagcdn.com/" + size + "x" + h + "/" + cc.toLowerCase() + ".png"}
      alt="" width={size} height={h}
      style={{ objectFit: "cover", borderRadius: 1, flexShrink: 0, verticalAlign: "middle" }}
      onError={(e) => { e.target.style.display = "none"; }} />
  );
}

function getStatusColor(status) {
  if (status === "Live") return "#E53935";
  if (status === "FT") return "rgba(255,255,255,0.4)";
  return "rgba(255,255,255,0.3)";
}

function formatTickerDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function FixtureChip({ fixture }) {
  const homeTeam = apiIdToTeam[fixture.homeTeamId];
  const awayTeam = apiIdToTeam[fixture.awayTeamId];
  const isLive = fixture.status === "Live";
  const isFT = fixture.status === "FT";
  const isNS = fixture.status === "NS";

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "0 18px", borderRight: "1px solid rgba(255,255,255,0.1)", flexShrink: 0 }}>
      {/* Home */}
      {homeTeam && <Flag cc={homeTeam.cc} size={14} />}
      <span style={{ fontWeight: 600, fontSize: 12, color: isLive ? "#FFD700" : "rgba(255,255,255,0.85)" }}>
        {fixture.homeTeam}
      </span>

      {/* Score or time */}
      {isNS ? (
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: "0 2px" }}>
          {formatTickerDate(fixture.date)}
        </span>
      ) : (
        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: isLive ? "#FFD700" : "white", margin: "0 3px", letterSpacing: 1 }}>
          {fixture.homeScore ?? 0}–{fixture.awayScore ?? 0}
        </span>
      )}

      {/* Away */}
      <span style={{ fontWeight: 600, fontSize: 12, color: isLive ? "#FFD700" : "rgba(255,255,255,0.85)" }}>
        {fixture.awayTeam}
      </span>
      {awayTeam && <Flag cc={awayTeam.cc} size={14} />}

      {/* Status badge */}
      <span style={{ fontSize: 10, fontWeight: 700, color: getStatusColor(fixture.status), marginLeft: 2 }}>
        {isLive ? `🔴 ${fixture.elapsed ? fixture.elapsed + "'" : "LIVE"}` : isFT ? "FT" : ""}
      </span>
    </span>
  );
}

function SectionMarker({ text, live }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "0 18px", borderRight: "1px solid rgba(255,255,255,0.1)", flexShrink: 0 }}>
      <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 12, letterSpacing: 2, color: live ? "#E53935" : "#FFD700", whiteSpace: "nowrap" }}>
        {live && "🔴 "}{text}
      </span>
    </span>
  );
}

function ScoreTicker({ inline = false }) {
  const [tickerFixtures, setTickerFixtures] = useState([]);
  const [label, setLabel] = useState("");
  const [paused, setPaused] = useState(false);
  const animRef = useRef(null);

  useEffect(() => {
    fetchTicker();
    const interval = setInterval(fetchTicker, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchTicker = async () => {
    try {
      const snap = await getDocs(collection(db, "fixtures"));
      const all = snap.docs.map((d) => d.data()).sort((a, b) => new Date(a.date) - new Date(b.date));

      const now = new Date();
      const todayStr = now.toLocaleDateString("en-CA"); // YYYY-MM-DD in local time

      // Today's fixtures
      const todayFixtures = all.filter((f) => {
        if (!f.date) return false;
        return new Date(f.date).toLocaleDateString("en-CA") === todayStr;
      });

      // Next upcoming day's fixtures (after today)
      const upcoming = all.filter((f) => f.status === "NS" && new Date(f.date).toLocaleDateString("en-CA") > todayStr);
      let nextDayFixtures = [];
      if (upcoming.length > 0) {
        const nextDateStr = new Date(upcoming[0].date).toLocaleDateString("en-CA");
        nextDayFixtures = upcoming.filter((f) => new Date(f.date).toLocaleDateString("en-CA") === nextDateStr);
      }

      // Build combined list with section markers so the ticker alternates today + next up
      const combined = [];
      if (todayFixtures.length > 0) {
        const hasLive = todayFixtures.some((f) => f.status === "Live");
        const allFT = todayFixtures.every((f) => f.status === "FT");
        const todayLabel = hasLive ? "LIVE" : allFT ? "TODAY'S RESULTS" : "TODAY";
        combined.push({ _marker: true, text: todayLabel, live: hasLive });
        todayFixtures.forEach((f) => combined.push(f));
      }
      if (nextDayFixtures.length > 0) {
        const nd = new Date(nextDayFixtures[0].date);
        const ndLabel = "NEXT UP · " + nd.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
        combined.push({ _marker: true, text: ndLabel, live: false });
        nextDayFixtures.forEach((f) => combined.push(f));
      }

      setTickerFixtures(combined);
      // Header label reflects the first section
      if (combined.length > 0 && combined[0]._marker) setLabel(combined[0].text);
      else setLabel("");
    } catch (e) {
      console.error("Ticker fetch failed:", e);
    }
  };

  if (tickerFixtures.length === 0) return null;

  const doubled = [...tickerFixtures, ...tickerFixtures];
  const speed = Math.max(tickerFixtures.length * 8, 20);

  if (inline) {
    return (
      <div
        style={{ overflow: "hidden", position: "relative", height: 44, display: "flex", alignItems: "center", borderRadius: 6 }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Scrolling fixtures - right to left */}
        <div style={{
          display: "flex", alignItems: "center", height: "100%",
          animation: `ticker-scroll ${speed}s linear infinite`,
          animationPlayState: paused ? "paused" : "running",
          whiteSpace: "nowrap",
        }}>
          {doubled.map((f, i) => f._marker ? <SectionMarker key={"m-" + i} text={f.text} live={f.live} /> : <FixtureChip key={f.fixtureId + "-" + i} fixture={f} />)}
        </div>

        {/* Left fade only — section labels now scroll inline as markers */}
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 30, background: "linear-gradient(90deg, rgba(10,22,40,1) 0%, transparent 100%)", pointerEvents: "none", zIndex: 10 }} />

        {/* Right fade */}
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 40, background: "linear-gradient(270deg, rgba(10,22,40,1) 0%, rgba(10,22,40,0.95) 60%, transparent 100%)", pointerEvents: "none", zIndex: 10 }} />

        <style>{`@keyframes ticker-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
      </div>
    );
  }

  return (
    <div
      style={{ background: "rgba(0,0,0,0.3)", borderBottom: "1px solid rgba(255,255,255,0.06)", overflow: "hidden", position: "relative", height: 32 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Left fade only — section labels now scroll inline as markers */}
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 30, zIndex: 10, background: "linear-gradient(90deg, rgba(10,22,40,1) 0%, transparent 100%)", pointerEvents: "none" }} />

      {/* Scrolling content */}
      <div
        ref={animRef}
        style={{
          display: "flex", alignItems: "center", height: "100%",
          paddingLeft: 12,
          animation: `ticker-scroll ${speed}s linear infinite`,
          animationPlayState: paused ? "paused" : "running",
          whiteSpace: "nowrap",
        }}
      >
        {doubled.map((f, i) => (
          f._marker
            ? <SectionMarker key={"m-" + i} text={f.text} live={f.live} />
            : <FixtureChip key={f.fixtureId + "-" + i} fixture={f} />
        ))}
      </div>

      {/* Fade edges */}
      <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 40, background: "linear-gradient(270deg, rgba(10,22,40,1) 0%, rgba(10,22,40,0.95) 60%, transparent 100%)", pointerEvents: "none" }} />

      <style>{`
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

export default ScoreTicker;
